// Weekly Pipeline Board — Apple × JARVIS live command centre. Three switchable
// board views (Executive / Timeline / Bento), a + on every week, live rings,
// still-needed dials, Daily Activity Entries, periods and the live feed.
// Every figure is real system data from /gameplan/board + /gameplan/live/overview.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Plus, Upload, LayoutDashboard, GitBranch, LayoutGrid, Trash2, Pencil } from 'lucide-react';
import { cn } from '../../lib/utils';
import { getStageLabel } from './gameplanUtils';
import { ActivityEntryPanel } from './ActivityEntryPanel';
import OverviewHero from './jarvis/OverviewHero';
import ExecKpiStrip from './jarvis/ExecKpiStrip';
import PipelineHealth from './jarvis/PipelineHealth';
import { Periods, Feed } from './jarvis/PeriodsFeed';
import { Dial, Roll, dollars, money } from './jarvis/Ring';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const WEEK_META = {
  1: { label: 'File preparation', tone: 'var(--green)', dc: '#30d158' },
  2: { label: 'Lodgement · assessment', tone: 'var(--blue)', dc: '#0a84ff' },
  3: { label: 'Assessment · MIRs', tone: 'var(--violet)', dc: '#bf5af2' },
  4: { label: 'Approval · settlement', tone: 'var(--amber)', dc: '#ffb340' },
};

const statusOf = (deal) => {
  const h = (deal.credit?.health || '').toUpperCase();
  if (h === 'OVERDUE') return { k: 'red', t: 'Overdue' };
  if (h === 'AT RISK') return { k: 'amber', t: 'At risk' };
  if (h === 'ACTION NEEDED') return { k: 'amber', t: 'Needs attention' };
  if (h === 'WAITING') return { k: 'blue', t: 'Waiting' };
  if (h === 'STALE') return { k: 'grey', t: 'Needs triage' };
  if ((deal.status_stage || '') === 'settled') return { k: 'green', t: 'Settled' };
  if ((deal.status_stage || '') === 'approved') return { k: 'green', t: 'Approved' };
  return { k: 'green', t: 'On track' };
};
const ownerOf = (deal) => deal.credit?.owner || deal.credit?.waiting || deal.broker || 'Unassigned';

const FileCard = ({ deal, onOpen, onDelete, onEdit, glow }) => {
  const s = statusOf(deal);
  return (
    <article role="button" tabIndex={0} onClick={() => onOpen?.(deal)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen?.(deal); }}
      className={cn('gpj-fc', `st-${s.k}`, glow && 'glow')} data-testid={`gp-file-${deal.id}`} data-deal-id={deal.id}>
      <div className="gpj-fc-top"><strong>{deal.client_name}</strong><span className={`gpj-badge gpj-b-${s.k}`}>{s.t}</span></div>
      <div className="gpj-fc-amt">{dollars(deal.est_loan_amount)}</div>
      <div className="gpj-fc-sub">{getStageLabel(deal.status_stage)}{deal.loan_type ? ` · ${deal.loan_type}` : ''}{deal.lender ? ` · ${deal.lender}` : ''}</div>
      <div className="gpj-fc-foot">
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen?.(deal); }}>{deal.credit?.action || 'Review file'}</button>
        <span className="gpj-owner">{ownerOf(deal)}</span>
      </div>
      <div className="gpj-fc-tools">
        {onEdit && <button type="button" title="Edit" onClick={(e) => { e.stopPropagation(); onEdit(deal); }} data-testid={`gp-file-edit-${deal.id}`}><Pencil /></button>}
        {onDelete && <button type="button" title="Delete" onClick={(e) => { e.stopPropagation(); onDelete(deal.id, deal.client_name); }} data-testid={`gp-file-delete-${deal.id}`}><Trash2 /></button>}
      </div>
    </article>
  );
};

const WeekPanel = ({ week, deals, lodged, count, monthlyTarget, current, onOpenFile, onAddDealToWeek, onDeleteDeal, onEditDeal, glowIds }) => {
  const meta = WEEK_META[week];
  const over = deals.filter((d) => (d.credit?.health || '').toUpperCase() === 'OVERDUE').length;
  const risk = deals.filter((d) => ['AT RISK', 'ACTION NEEDED'].includes((d.credit?.health || '').toUpperCase())).length;
  const share = monthlyTarget ? monthlyTarget / 4 : 0;
  const pct = share ? Math.min(100, (lodged / share) * 100) : 0;
  return (
    <div className={cn('gpj-week', current && 'current')} data-testid={`gp-week-${week}`}>
      <div className="gpj-wh">
        <div className="gpj-wt">
          <span className="gpj-wdot" style={{ background: meta.tone }} />
          <div><strong>Week {week}</strong><small>{meta.label}</small></div>
          {onAddDealToWeek && <button type="button" className="gpj-wadd" onClick={() => onAddDealToWeek(week)} title={`Add a file to Week ${week}`} data-testid={`gp-week-add-${week}`}><Plus /></button>}
        </div>
        <div className="gpj-wval"><strong><Roll value={dollars(lodged)} /></strong><span>{count} lodged · {deals.length} files</span></div>
        <div className="gpj-wlbl">Lodged this week</div>
        {share > 0 && <div className={cn('gpj-wbar', pct >= 100 && 'done')}><i style={{ width: `${pct}%` }} /></div>}
        <div className="gpj-wstat">
          <span className={cn('gpj-sd', over ? 'red' : risk ? 'amber' : '')} />
          {over || risk ? `${over ? `${over} overdue` : ''}${over && risk ? ' · ' : ''}${risk ? `${risk} at risk` : ''}` : 'All on track'}
        </div>
      </div>
      <div className="gpj-wlist">
        {deals.length === 0
          ? (onAddDealToWeek
              ? <button type="button" className="gpj-addfirst" onClick={() => onAddDealToWeek(week)} data-testid={`gp-week-empty-${week}`}>+ Add first file</button>
              : <div className="gpj-addfirst" style={{ cursor: 'default', opacity: 0.6 }} data-testid={`gp-week-empty-${week}`}>No files</div>)
          : deals.map((d) => <FileCard key={d.id} deal={d} onOpen={onOpenFile} onDelete={onDeleteDeal} onEdit={onEditDeal} glow={glowIds?.has(d.id)} />)}
      </div>
    </div>
  );
};

function LodgeApproveNeeded({ authToken, refreshSignal, month }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let on = true;
    axios.get(`${API_BASE}/api/gameplan/lodge-approve-needed`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => { if (on) setD(r.data); }).catch(() => {});
    return () => { on = false; };
  }, [authToken, refreshSignal]);
  if (!d) return null;
  const cols = [['Today', 'day'], ['This week', 'week'], ['This month', 'month']];
  return (
    <section className="gpj-panel" data-testid="gp-lodge-approve-needed">
      <div className="gpj-panel-h"><h2>Still needed to hit target</h2><p>{d.target_set ? d.month : 'Set a monthly target in Set Goals'}</p></div>
      <div className="gpj-ncols">
        {cols.map(([h, k]) => (
          <div className="gpj-ncol" key={k}>
            <small>{h}</small>
            <Dial remain={d.lodge[k].remaining} done={d.lodge[k].done} target={d.lodge[k].target} label="Files to lodge" testid={`gp-gauge-lodge-${k}`} />
            <Dial remain={d.approve[k].remaining} done={d.approve[k].done} target={d.approve[k].target} label="Files to approve" green testid={`gp-gauge-approve-${k}`} />
          </div>
        ))}
      </div>
    </section>
  );
}

const VIEW_OPTIONS = [
  { key: 'executive', label: 'Executive', icon: LayoutDashboard },
  { key: 'timeline', label: 'Timeline', icon: GitBranch },
  { key: 'bento', label: 'Bento', icon: LayoutGrid },
];

const currentWeekOf = (month) => {
  const now = new Date();
  if (`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}` !== month) return 0;
  const d = now.getDate();
  return d <= 7 ? 1 : d <= 14 ? 2 : d <= 21 ? 3 : 4;
};

const PremiumBoard = ({ month, boardData, live, viewMode, setViewMode, authToken, refreshSignal, readOnly = false,
  onOpenFile, onAddDealTop, onAddDealToWeek, onImport, onDeleteDeal, onEditDeal, onActivitySaved, onStage, boardOnly = false }) => {
  const weeks = [1, 2, 3, 4];
  const totals = boardData?.totals || {};
  const board = boardData?.board || {};
  const monthlyTarget = Number(boardData?.monthly_target_amount || 0);
  const cur = currentWeekOf(month);
  const activeFiles = weeks.reduce((s, w) => s + (board[w] || []).length, 0);
  const atRisk = useMemo(() => weeks.reduce((s, w) => s + (board[w] || []).filter((d) => ['OVERDUE', 'AT RISK'].includes((d.credit?.health || '').toUpperCase())).length, 0), [board]);

  // Cards whose stage advanced since the last poll glow briefly (real-time pulse).
  const prev = useRef(null);
  const [glowIds, setGlowIds] = useState(new Set());
  useEffect(() => {
    const sig = {};
    weeks.forEach((w) => (board[w] || []).forEach((d) => { sig[d.id] = `${d.status_stage}|${d.approved_date || ''}|${d.settled_date || ''}`; }));
    if (prev.current) {
      const changed = new Set(Object.keys(sig).filter((id) => prev.current[id] !== undefined && prev.current[id] !== sig[id]));
      if (changed.size) { setGlowIds(changed); setTimeout(() => setGlowIds(new Set()), 2000); }
    }
    prev.current = sig;
  }, [board]);

  const Board = () => (
    <div className="gpj-weeks">
      {weeks.map((w) => (
        <WeekPanel key={w} week={w} deals={board[w] || []} lodged={totals[w]?.amount || 0} count={totals[w]?.count || 0} current={cur === w}
          monthlyTarget={monthlyTarget} onOpenFile={onOpenFile} onAddDealToWeek={onAddDealToWeek} onDeleteDeal={onDeleteDeal} onEditDeal={onEditDeal} glowIds={glowIds} />
      ))}
    </div>
  );

  const boardPanel = (
      <section className="gpj-panel" data-testid="gpj-board-panel">
        <div className="gpj-board-bar">
          <h2>Weekly Pipeline Board</h2>
          <div className="gpj-seg" data-testid="gp-view-switcher">
            {VIEW_OPTIONS.map(({ key, label, icon: Icon }) => (
              <button key={key} type="button" aria-pressed={viewMode === key} onClick={() => setViewMode(key)} data-testid={`gp-view-${key}`}><Icon /> <span className="hidden sm:inline">{label}</span></button>
            ))}
          </div>
          <span className="sp" />
          {!readOnly && <button type="button" className="gpj-btn" onClick={onImport} data-testid="gp-import-btn"><Upload /> Import</button>}
          {!readOnly && <button type="button" className="gpj-btn primary" onClick={onAddDealTop} data-testid="gp-add-deal-btn"><Plus /> Add file</button>}
        </div>

        {viewMode === 'timeline' && (
          <div className="gpj-tl" data-testid="gp-timeline-strip">
            <div className="gpj-tl-line" />
            <div className="gpj-tl-grid">
              {weeks.map((w) => (
                <div key={w}>
                  <span className="gpj-tl-dot" style={{ background: WEEK_META[w].dc, '--dc': WEEK_META[w].dc }} />
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 800 }}>Week {w}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-3)' }}>{WEEK_META[w].label}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{dollars(totals[w]?.amount || 0)}</div>
                    <div style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>{totals[w]?.count || 0} lodged · {(board[w] || []).length} files</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'bento' && (
          <div className="gpj-kpis" data-testid="gp-bento-kpis">
            <div className="gpj-kpi"><small>Weekly target</small><strong>{boardData?.weekly_lodgement_target || 0}</strong><em>lodgements/wk</em></div>
            <div className="gpj-kpi"><small>Active files</small><strong>{activeFiles}</strong><em>on this month's board</em></div>
            <div className="gpj-kpi"><small>At risk · overdue</small><strong style={{ color: atRisk ? 'var(--amber)' : 'var(--fg)' }}>{atRisk}</strong><em>need attention</em></div>
            <div className="gpj-kpi"><small>This week $</small><strong>{money(totals[cur || 1]?.amount || 0)}</strong><em>Week {cur || 1} lodged</em></div>
            <div className="gpj-kpi"><small>Settled this month</small><strong>{money(live?.settled?.amount)}</strong><em>{live?.settled?.count || 0} files</em></div>
          </div>
        )}

        <Board />
      </section>
  );

  // Command Centre embeds the Weekly Pipeline Board exactly as-is, without the surrounding panels.
  if (boardOnly) return boardPanel;

  return (
    <div className="gpj-grid" data-testid="gp-premium-board">
      <ExecKpiStrip boardData={boardData} live={live} />
      <OverviewHero boardData={boardData} live={live} month={month} />

      {boardPanel}

      <div className="gpj-ov-pair">
        <PipelineHealth live={live} onStage={onStage} />
        <LodgeApproveNeeded authToken={authToken} refreshSignal={refreshSignal} month={month} />
      </div>

      <section className="gpj-panel" data-testid="gp-daily-activity">
        <ActivityEntryPanel authToken={authToken} month={month} refreshSignal={refreshSignal} onSaved={onActivitySaved} bare />
      </section>

      <div className="gpj-grid gpj-two">
        <Periods live={live} />
        <Feed live={live} />
      </div>
    </div>
  );
};

export default PremiumBoard;
export { LodgeApproveNeeded };
